import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ExternalLink,
  Headphones,
  RefreshCw,
  ShieldCheck,
  Users,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useClientStore } from '@/client/stores/client';

type SupportDashboard = {
  overview: {
    totalOpen: number;
    totalInProgress: number;
    totalResolved: number;
    totalClosed: number;
    avgFirstResponseMs: number | null;
    avgResolutionMs: number | null;
    volumeByDay: { date: string; count: number }[];
  };
  levels: {
    level: 'L1' | 'L2' | 'L3';
    assignedUsers: number;
    onlineUsers: number;
    availableUsers: number;
    activeTickets: number;
    capacity: number;
  }[];
  channels: { channel: string; count: number }[];
  generatedAt: string;
};

const unwrap = <T,>(value: any): T =>
  value && typeof value === 'object' && 'data' in value ? value.data : value;

const duration = (milliseconds: number | null) => {
  if (milliseconds == null) return 'No data';
  const minutes = Math.round(milliseconds / 60000);
  if (minutes < 60) return `${minutes} min`;
  return `${(minutes / 60).toFixed(1)} hr`;
};

const fmtDateTime = (iso: string) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

function HealthRow({
  label,
  ok,
  detail,
}: {
  label: string;
  ok: boolean;
  detail: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg bg-gray-50 px-3 py-2.5">
      <span
        className={`mt-1 h-2.5 w-2.5 rounded-full ${
          ok ? 'bg-green-500' : 'bg-amber-500'
        }`}
      />
      <div>
        <p className="text-sm font-semibold text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{detail}</p>
      </div>
    </div>
  );
}

export default function SupportAnalyticsPage() {
  useBreadcrumbs([{ label: 'Support' }, { label: 'Analytics' }]);
  const request = useClientStore((state) => state.request);
  const query = useQuery({
    queryKey: ['support-analytics'],
    queryFn: async () =>
      unwrap<SupportDashboard>(await request('/api/admin/v2/support/analytics')),
  });
  const dashboard = query.data;
  const supportUrl =
    import.meta.env.VITE_SUPPORT_APP_URL || 'https://support.feelyfeely.cloud';
  const totalActive =
    (dashboard?.overview.totalOpen ?? 0) +
    (dashboard?.overview.totalInProgress ?? 0);
  const totalCapacity =
    dashboard?.levels.reduce((sum, level) => sum + level.capacity, 0) ?? 0;
  const pressurePct = totalCapacity > 0 ? Math.round((totalActive / totalCapacity) * 100) : 0;
  const busiestLevel = dashboard?.levels
    .slice()
    .sort((a, b) => b.activeTickets - a.activeTickets)[0];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <Headphones className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Customer support</h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Ticket activity, response performance, and team availability.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700"
          >
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <a
            href={supportUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-900 px-3 py-2 text-sm font-semibold text-white"
          >
            Open support workspace <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </div>

      {query.isLoading ? (
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-sm text-gray-500">
          Loading support analytics…
        </div>
      ) : query.error || !dashboard ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
          Support analytics are temporarily unavailable. Confirm ff-api can reach ff-support-api.
        </div>
      ) : (
        <div className="space-y-5">
          <section className="grid gap-4 xl:grid-cols-[1.25fr_0.75fr]">
            <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-950 to-blue-800 p-5 text-white shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">
                    Support command center
                  </p>
                  <h2 className="mt-2 text-2xl font-bold">Live support overview</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-blue-100">
                    Monitor ticket volume, team capacity, and role coverage. Agents
                    continue day-to-day work in the dedicated support workspace.
                  </p>
                </div>
                <div className="rounded-2xl bg-white/10 px-4 py-3 text-right ring-1 ring-white/15">
                  <p className="text-xs text-blue-100">Queue pressure</p>
                  <p className="mt-1 text-3xl font-bold">{pressurePct}%</p>
                  <p className="text-xs text-blue-100">
                    {totalActive} active / {totalCapacity || '—'} capacity
                  </p>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <a
                  href={supportUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-blue-900"
                >
                  Open workspace <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href="/admin/users/roles"
                  className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white ring-1 ring-white/20"
                >
                  Manage support roles
                </a>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-green-600" />
                <h2 className="font-semibold text-gray-900">Support system health</h2>
              </div>
              <div className="mt-4 space-y-2">
                <HealthRow
                  label="ff-api analytics bridge"
                  ok={!query.error}
                  detail={!query.error ? 'ff-admin can load support analytics.' : 'Analytics bridge is unavailable.'}
                />
                <HealthRow
                  label="Support workspace URL"
                  ok={Boolean(supportUrl)}
                  detail={supportUrl}
                />
                <HealthRow
                  label="Latest analytics snapshot"
                  ok={Boolean(dashboard.generatedAt)}
                  detail={fmtDateTime(dashboard.generatedAt)}
                />
              </div>
            </div>
          </section>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ['Open tickets', dashboard.overview.totalOpen],
              ['In progress', dashboard.overview.totalInProgress],
              ['Average first response', duration(dashboard.overview.avgFirstResponseMs)],
              ['Average resolution', duration(dashboard.overview.avgResolutionMs)],
            ].map(([label, value]) => (
              <div key={label} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="text-sm text-gray-500">{label}</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-5 xl:grid-cols-[1.4fr_1fr]">
            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-semibold text-gray-900">Ticket volume · last 30 days</h2>
                <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                  {dashboard.overview.volumeByDay.reduce((sum, row) => sum + row.count, 0)} total
                </span>
              </div>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboard.overview.volumeByDay}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#1e3a8a" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-700" />
                <h2 className="font-semibold text-gray-900">Tickets by channel</h2>
              </div>
              <div className="mt-4 space-y-3">
                {dashboard.channels.map((channel) => (
                  <div key={channel.channel} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                    <span className="text-sm capitalize text-gray-600">{channel.channel.replace('_', ' ')}</span>
                    <span className="font-semibold text-gray-900">{channel.count}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-700" />
                <div>
                  <h2 className="font-semibold text-gray-900">Support levels</h2>
                  <p className="text-xs text-gray-500">
                    Busiest level: {busiestLevel?.level ?? '—'} · {busiestLevel?.activeTickets ?? 0} active tickets
                  </p>
                </div>
              </div>
              <a href="/admin/users/roles" className="text-sm font-semibold text-blue-700 hover:underline">
                Manage role assignments
              </a>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    {['Level', 'Assigned users', 'Online', 'Available', 'Active tickets', 'Capacity'].map((heading) => (
                      <th key={heading} className="px-5 py-3 font-semibold">{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dashboard.levels.map((level) => (
                    <tr key={level.level}>
                      <td className="px-5 py-4 font-semibold text-gray-900">{level.level}</td>
                      <td className="px-5 py-4">{level.assignedUsers}</td>
                      <td className="px-5 py-4">{level.onlineUsers}</td>
                      <td className="px-5 py-4">{level.availableUsers}</td>
                      <td className="px-5 py-4">{level.activeTickets}</td>
                      <td className="px-5 py-4">
                        <div className="flex min-w-[160px] items-center gap-2">
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className={`h-full rounded-full ${
                                level.capacity > 0 && level.activeTickets / level.capacity >= 0.9
                                  ? 'bg-red-500'
                                  : level.capacity > 0 && level.activeTickets / level.capacity >= 0.7
                                    ? 'bg-amber-500'
                                    : 'bg-blue-600'
                              }`}
                              style={{
                                width: `${Math.min(
                                  100,
                                  level.capacity > 0
                                    ? Math.round((level.activeTickets / level.capacity) * 100)
                                    : 0,
                                )}%`,
                              }}
                            />
                          </div>
                          <span className="text-xs font-semibold text-gray-600">
                            {level.activeTickets}/{level.capacity}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
